/**
 * Thin typed fetch wrapper for the Worker API.
 *
 * Calls are same-origin (`/api/*`, FR-AP-P1), so there is no CORS handling and
 * no base URL to configure. Two failure modes are modelled explicitly because
 * the UI reacts to them differently:
 *
 * - `403` — the server denied the request. The Worker answers every denial
 *   identically whether the caller is unauthenticated, under-privileged, or
 *   asking for a record that does not exist (BR-AP1), so the client must not
 *   try to infer which, and neither may the UI.
 * - `501` — the endpoint is scaffolded but not built yet. Admin screens render
 *   a "planned" state for this instead of an error.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isDenied(): boolean {
    return this.status === 403;
  }

  get isNotImplemented(): boolean {
    return this.status === 501;
  }
}

const API_ROOT = '/api';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  let response: Response;
  try {
    response = await fetch(`${API_ROOT}${path}`, {
      method,
      // Cloudflare Access sets CF_Authorization as a first-party cookie; it has
      // to ride along or the Worker sees an anonymous request.
      credentials: 'same-origin',
      ...(body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(0, 'The API is unreachable. Check your connection and try again.');
  }

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Request failed with status ${response.status}`;
    throw new ApiError(response.status, message);
  }

  return payload as T;
}
